import { CalculationExplanationBox } from './CalculationExplanationBox';
import { isSelectedRevenueDriver } from '../../types/driverTerms';
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useHideAssistantLauncher } from '../../../../components/AIChat/launcherVisibility';
import { X, ExternalLink, PanelRightClose, Clock, Info, MessageSquare, AlertCircle, Loader2, Calendar, Search, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { DriverBadge } from '../shared/DriverBadge';
import { formatCurrency, formatRelativeTime, getInitials } from '../../utils/formatters';
import { AdjustmentsSection } from './AdjustmentsSection';
import { AnalysisTab } from './AnalysisTab';
import { getAccountAnalysis, getRevenueDepartments } from '../../api';
// @ts-ignore - JavaScript module without types
import { useUserSettingsContext } from '../../../../context/UserSettingsContext';
import type { ForecastAccount, DetailPanelTab, DriverType, ZeroBaseDriverLayer, LaborPosition, LaborPositionConfig } from '../../types';
import { MONTH_LABELS, DRIVER_CONFIG } from '../../types';
import { ZeroBaseDriverModal } from './ZeroBaseDriverModal';
import { LaborForecastDriverModal } from './LaborForecastDriverModal';
import { SourceAccountPicker } from './SourceAccountPicker';
import { ownerWindowOf } from '../../utils/ownerWindow';
import { DriverSelect } from './DriverSelect';
import { LY_BUDGET_DRIVER_BASES } from '../../types/driverTerms';
import { getVersionDriverEntry } from './driverCatalog';
// @ts-ignore - JavaScript module without types
import { getPositions } from '../../../../utils/positionsApi';
// @ts-ignore - JavaScript module without types
import API_ENDPOINTS from '../../../../config/apiConfig';
// @ts-ignore - JavaScript module without types
import { api } from '../../../../utils/api';

interface AccountDetailPanelProps {
  account: ForecastAccount;
  accounts?: ForecastAccount[];
  onClose: () => void;
  onDriverChange?: (driverType: DriverType, input: number | null, selectedDepartments?: string[], clearOverrides?: boolean, fallbackDriverType?: 'Budget' | 'LY Same Month' | null, sourceChartOfAccountIds?: number[], zeroBaseDriverLayers?: ZeroBaseDriverLayer[], laborPositionConfigs?: LaborPositionConfig[]) => Promise<void>;
  onOverrideChange?: (monthIndex: number, value: number | null) => Promise<void>;
  onOpenDailyEntry?: (account: ForecastAccount, month: number) => void;
  /** Bumped by the parent to open the Labor Config modal for `account` (e.g. grid sub-row click) */
  laborModalRequest?: { chartOfAccountId: number; nonce: number } | null;
  isReadOnly?: boolean;
  versionId?: number;
  locationId?: number;
  selectedYear?: number;
  isRecalculating?: boolean;
  onViewTransactions?: (chartOfAccountId: number, accountName: string, accountNumber: string, month: number, columnType: 'Actual' | 'LY') => void;
  occupiedRoomsPerMonth?: Record<string, number>;
  availableRoomsPerMonth?: Record<string, number>;
  currency?: string | null;
  /** Rendered in a popped-out window (full width, dock control). */
  isPoppedOut?: boolean;
  onTogglePopout?: () => void;
}

export function AccountDetailPanel({
  account,
  accounts = [],
  onClose,
  onDriverChange,
  onOverrideChange,
  onOpenDailyEntry,
  laborModalRequest,
  isReadOnly = false,
  versionId,
  locationId,
  selectedYear = new Date().getFullYear(),
  isRecalculating = false,
  onViewTransactions,
  occupiedRoomsPerMonth,
  availableRoomsPerMonth,
  currency = 'USD',
  isPoppedOut = false,
  onTogglePopout,
}: AccountDetailPanelProps) {
  const { state, setDetailPanelTab } = useForecastV3();
  const { detailPanelTab, orgId, year, currentVersion } = state;
  // Docked on the right, the panel's note composer sits under the floating
  // assistant button; the toolbar's AI button covers the same ground.
  useHideAssistantLauncher(!isPoppedOut);

  // A grid-originated labor-config request must land on the Driver tab, where the modal lives
  useEffect(() => {
    if (laborModalRequest && laborModalRequest.chartOfAccountId === account.chartOfAccountId) setDetailPanelTab('driver');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborModalRequest?.nonce]);
  const isBudgetVersion = currentVersion?.type === 'Budget';
  // Fetch events to get last activity info
  const { useAccountEvents } = require('../../hooks/useAccountEvents');
  const { events } = useAccountEvents({
    orgId: orgId || 0,
    locationId: locationId || 0,
    versionId: versionId || 0,
    accountId: account.chartOfAccountId,
    year,
    limit: 1, // Only need the most recent
    enabled: !!orgId && !!locationId && !!versionId,
  });

  // Get the most recent event for "last changed by" info
  const mostRecentEvent = events && events.length > 0 ? events[0] : null;

  // Determine the effective driver type (account's own or inherited from department)
  const effectiveDriverType = account.inheritsFromDepartment && account.departmentDriverType
    ? account.departmentDriverType
    : account.driverType;

  // Manual and Static Entry drivers don't support overrides - values are entered directly
  const isManualDriver = effectiveDriverType === 'Manual' || effectiveDriverType === 'Static Entry';

  // Filter tabs based on driver type
  const tabs: DetailPanelTab[] = isManualDriver
    ? ['driver', 'adjustments', 'analysis', 'notes']
    : ['driver', 'adjustments', 'analysis', 'overrides', 'notes'];

  // Get query client for prefetching
  const queryClient = useQueryClient();

  // Get the active month for the analysis tab
  const { activeMonth } = useMonthlyVersions();

  // Budget mode: the Analysis chart shows the whole plan ahead (active
  // month → Dec) as a line continuing the trailing 12 months of actuals.
  const forwardMonths = useMemo(() => {
    if (!isBudgetVersion || !activeMonth) return undefined;
    const out = [];
    for (let m = activeMonth; m <= 12; m++) {
      const label = MONTH_LABELS[m - 1];
      const value = account.monthlyValues[label.toLowerCase() as keyof typeof account.monthlyValues] ?? 0;
      out.push({
        month: `${year}-${String(m).padStart(2, '0')}`,
        displayMonth: new Date(year, m - 1, 1).toLocaleString('en', { month: 'short' }),
        year,
        monthNum: m,
        value: Number(value) || 0,
        isForecast: true,
      });
    }
    return out;
  }, [isBudgetVersion, activeMonth, account.monthlyValues, year]);

  // Prefetch analysis data when the panel opens (not when tab is clicked)
  useEffect(() => {
    if (orgId && locationId && account.chartOfAccountId && activeMonth && year) {
      queryClient.prefetchQuery({
        queryKey: ['accountAnalysis', orgId, locationId, account.chartOfAccountId, versionId, activeMonth, year],
        queryFn: () => getAccountAnalysis({
          orgId,
          locationId,
          chartOfAccountId: account.chartOfAccountId,
          versionId,
          forecastMonth: activeMonth,
          forecastYear: year,
        }),
        staleTime: 5 * 60 * 1000, // Match the AnalysisTab staleTime
      });
    }
  }, [queryClient, orgId, locationId, account.chartOfAccountId, versionId, activeMonth, year]);

  return (
    <div
      className={`${isPoppedOut ? 'w-full min-h-screen' : 'w-[420px] border-l'} border-slate-200 bg-white flex flex-col relative`}
      // The driver picker takes over this whole pane while open (DriverSelect
      // portals its list here), so the host must be a positioned box.
      data-driver-takeover-host=""
    >
      {/* Panel Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800">{account.accountName}</h3>
          <div className="flex items-center gap-0.5">
            {onTogglePopout && (
              <button
                onClick={onTogglePopout}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
                title={isPoppedOut ? 'Bring pane back into the workspace' : 'Pop out to a separate window'}
              >
                {isPoppedOut ? <PanelRightClose size={16} className="text-slate-400" /> : <ExternalLink size={16} className="text-slate-400" />}
              </button>
            )}
            {!isPoppedOut && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-slate-100 rounded transition-colors"
              >
                <X size={18} className="text-slate-400" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span>{account.accountType} • {account.department} Department</span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <DriverBadge
            driver={account.inheritsFromDepartment && account.departmentDriverType
              ? account.departmentDriverType
              : account.driverType
            }
          />
          {account.inheritsFromDepartment ? (
            <span className="text-xs text-green-600">↳ Inherited from department</span>
          ) : (
            <span className="text-xs text-slate-400">Applied to: All months</span>
          )}
        </div>
        {mostRecentEvent && (
          <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
            <Clock size={12} />
            Last changed by {mostRecentEvent.createdByName || mostRecentEvent.createdBy} • {formatRelativeTime(mostRecentEvent.createdAt)}
          </div>
        )}
      </div>

      {/* Panel Tabs */}
      <div className="flex" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setDetailPanelTab(tab)}
            className="flex-1 py-2.5 text-sm font-medium capitalize transition-colors border-b-2"
            style={detailPanelTab === tab
              ? { color: 'var(--lodge-orange-500)', borderBottomColor: 'var(--lodge-orange-400)', marginBottom: -1 }
              : { color: 'var(--lodge-onyx-500)', borderBottomColor: 'transparent', marginBottom: -1 }}
            onMouseEnter={e => { if (detailPanelTab !== tab) (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-800)'; }}
            onMouseLeave={e => { if (detailPanelTab !== tab) (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-onyx-500)'; }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Panel Content */}
      <div className="flex-1 overflow-auto p-4">
        {detailPanelTab === 'driver' && (
          <DriverTabContent
            account={account}
            accounts={accounts}
            onDriverChange={onDriverChange}
            onOpenDailyEntry={onOpenDailyEntry}
            laborModalRequest={laborModalRequest}
            isReadOnly={isReadOnly}
            orgId={orgId ?? undefined}
            locationId={locationId}
            onViewTransactions={onViewTransactions}
            occupiedRoomsPerMonth={occupiedRoomsPerMonth}
            availableRoomsPerMonth={availableRoomsPerMonth}
            currency={currency}
          />
        )}
        {detailPanelTab === 'adjustments' && versionId && locationId && (
          <AdjustmentsSection
            account={account}
            versionId={versionId}
            locationId={locationId}
            selectedYear={selectedYear}
            isReadOnly={isReadOnly}
            isRecalculating={isRecalculating}
            currency={currency}
          />
        )}
        {detailPanelTab === 'adjustments' && (!versionId || !locationId) && (
          <div className="text-center py-6 text-sm text-slate-400">
            Select a version to manage adjustments
          </div>
        )}
        {detailPanelTab === 'analysis' && orgId && locationId && activeMonth && (
          <AnalysisTab
            chartOfAccountId={account.chartOfAccountId}
            orgId={orgId}
            locationId={locationId}
            versionId={versionId}
            forecastMonth={activeMonth}
            forecastYear={year}
            currentForecastValue={
              activeMonth
                ? account.monthlyValues[MONTH_LABELS[activeMonth - 1].toLowerCase() as keyof typeof account.monthlyValues]
                : undefined
            }
            accountType={account.accountType}
            driverType={effectiveDriverType}
            initialSelectedDepartments={account.selectedDepartments}
            occupiedRoomsPerMonth={occupiedRoomsPerMonth}
            currency={currency}
            forwardMonths={forwardMonths}
            forwardLabel="Budget"
          />
        )}
        {detailPanelTab === 'analysis' && (!orgId || !locationId || !activeMonth) && (
          <div className="text-center py-6 text-sm text-slate-400">
            Select a month to view analysis
          </div>
        )}
        {detailPanelTab === 'overrides' && (
          <OverridesTabContent
            account={account}
            onOverrideChange={onOverrideChange}
            isReadOnly={isReadOnly}
          />
        )}
        {detailPanelTab === 'notes' && <NotesTabContent account={account} />}
      </div>
    </div>
  );
}

interface DriverTabContentProps {
  account: ForecastAccount;
  accounts?: ForecastAccount[];
  onDriverChange?: (driverType: DriverType, input: number | null, selectedDepartments?: string[], clearOverrides?: boolean, fallbackDriverType?: 'Budget' | 'LY Same Month' | null, sourceChartOfAccountIds?: number[], zeroBaseDriverLayers?: ZeroBaseDriverLayer[], laborPositionConfigs?: LaborPositionConfig[]) => Promise<void>;
  onOpenDailyEntry?: (account: ForecastAccount, month: number) => void;
  laborModalRequest?: { chartOfAccountId: number; nonce: number } | null;
  isReadOnly?: boolean;
  orgId?: number;
  locationId?: number;
  onViewTransactions?: (chartOfAccountId: number, accountName: string, accountNumber: string, month: number, columnType: 'Actual' | 'LY') => void;
  occupiedRoomsPerMonth?: Record<string, number>;
  availableRoomsPerMonth?: Record<string, number>;
  currency?: string | null;
}

function DriverTabContent({ account, accounts = [], onDriverChange, onOpenDailyEntry, laborModalRequest, isReadOnly = false, orgId, locationId, onViewTransactions, occupiedRoomsPerMonth, availableRoomsPerMonth, currency = 'USD' }: DriverTabContentProps) {
  const { state: { currentVersion, year } } = useForecastV3();
  const isBudgetVersion = currentVersion?.type === 'Budget';
  // Native dialogs must open in the window this pane lives in (it may be popped out).
  const tabRootRef = useRef<HTMLDivElement>(null);
  const { activeMonth } = useMonthlyVersions();
  // When inheriting from department, show department driver type for display
  // When account has its own driver, show that driver
  const getDisplayDriverType = (): DriverType => {
    if (account.inheritsFromDepartment && account.departmentDriverType) {
      return account.departmentDriverType as DriverType;
    }
    return account.driverType;
  };

  const [driverType, setDriverType] = useState<DriverType>(getDisplayDriverType());
  const driverFormType = LY_BUDGET_DRIVER_BASES[driverType] || driverType;
  const [driverInput, setDriverInput] = useState<string>(
    account.driverInput?.toString() || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  // State for "% of Selected Revenue" department selection
  interface RevenueDepartment {
    name: string;
    accountCount: number;
  }
  const [revenueDepartments, setRevenueDepartments] = useState<RevenueDepartment[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>(
    account.selectedDepartments || []
  );
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  // State for REVPAR Projection fallback driver
  const [fallbackDriverType, setFallbackDriverType] = useState<'Budget' | 'LY Same Month' | null>(
    account.fallbackDriverType || null
  );

  // State for "% of Account" source account selection
  const [sourceChartOfAccountIds, setSourceChartOfAccountIds] = useState<number[]>(
    account.sourceChartOfAccountIds || []
  );

  // State for Zero Base driver modal
  const [isZeroBaseModalOpen, setIsZeroBaseModalOpen] = useState(false);
  const [zeroBaseDriverLayers, setZeroBaseDriverLayers] = useState<ZeroBaseDriverLayer[]>(
    account.zeroBaseDriverLayers || []
  );

  // State for Labor Config driver modal
  const [isLaborModalOpen, setIsLaborModalOpen] = useState(false);
  const [laborPositions, setLaborPositions] = useState<LaborPosition[]>([]);
  const [isLoadingLaborPositions, setIsLoadingLaborPositions] = useState(false);
  const [laborPositionConfigs, setLaborPositionConfigs] = useState<LaborPositionConfig[]>(
    account.laborPositionConfigs || []
  );
  // Revenue department names for the Labor Config "% of Revenue" scope picker
  const laborRevenueDepartments = useMemo(
    () => Array.from(new Set(accounts.filter(a => a.accountType === 'Revenue').map(a => a.department))).sort(),
    [accounts]
  );

  // Check if current driver requires department selection
  const requiresDepartmentSelection = isSelectedRevenueDriver(driverType);

  // Fetch revenue departments when needed
  useEffect(() => {
    if (requiresDepartmentSelection && orgId && locationId && revenueDepartments.length === 0) {
      setIsLoadingDepartments(true);
      getRevenueDepartments({ orgId, locationId })
        .then((response) => {
          setRevenueDepartments(response.departments || []);
        })
        .catch((err) => {
          console.error('Failed to fetch revenue departments:', err);
          toast.error('Failed to load revenue departments');
        })
        .finally(() => {
          setIsLoadingDepartments(false);
        });
    }
  }, [requiresDepartmentSelection, orgId, locationId, revenueDepartments.length]);

  // Sync local state when account changes (fixes stale state when switching accounts)
  useEffect(() => {
    const displayType = account.inheritsFromDepartment && account.departmentDriverType
      ? account.departmentDriverType as DriverType
      : account.driverType;
    setDriverType(displayType);
    setDriverInput(account.driverInput?.toString() || '');
    setSelectedDepartments(account.selectedDepartments || []);
    setFallbackDriverType(account.fallbackDriverType || null);
    setSourceChartOfAccountIds(account.sourceChartOfAccountIds || []);
    setZeroBaseDriverLayers(account.zeroBaseDriverLayers || []);
    setLaborPositionConfigs(account.laborPositionConfigs || []);
    // Positions are per-account (fetched by chartOfAccountId) — clear them so the
    // labor modal refetches instead of showing the previous account's positions
    setLaborPositions([]);
  }, [account.id, account.driverType, account.driverInput, account.inheritsFromDepartment, account.departmentDriverType, account.selectedDepartments, account.fallbackDriverType, account.sourceChartOfAccountIds, account.zeroBaseDriverLayers, account.laborPositionConfigs]);

  // Parse input value
  const parseInput = (value: string): number | null => {
    const cleaned = value.replace(/[$%,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  // Handle driver type change
  const handleDriverTypeChange = async (newType: DriverType) => {
    if (!onDriverChange) return;

    // Check if we're switching away from Manual driver - warn user that values will be cleared
    const currentEffectiveDriver = account.inheritsFromDepartment && account.departmentDriverType
      ? account.departmentDriverType
      : account.driverType;
    const isLeavingManual = currentEffectiveDriver === 'Manual' && newType !== 'Manual';

    // Check if account has any override values (manual entries)
    const hasOverrides = account.monthlyOverrides && Object.values(account.monthlyOverrides).some(v => v === true);

    // Show warning if leaving Manual driver and there are values that will be lost
    if (isLeavingManual && hasOverrides) {
      const confirmed = ownerWindowOf(tabRootRef.current).confirm(
        'Changing from Manual Entry will clear all manually entered values for this account. This cannot be undone.\n\nAre you sure you want to continue?'
      );
      if (!confirmed) {
        return; // User cancelled, don't change driver
      }
    }

    setDriverType(newType);

    // For Zero Base, open the modal instead of immediately saving
    if (newType === 'Zero Base') {
      setIsZeroBaseModalOpen(true);
      return;
    }

    // For Labor Config, open the modal instead of immediately saving
    if (newType === 'Labor Config') {
      openLaborModal();
      return;
    }

    setIsSaving(true);
    try {
      // Pass selectedDepartments for drivers that require department selection
      const depts = isSelectedRevenueDriver(newType) ? selectedDepartments : undefined;
      // Clear overrides when switching from Manual driver
      const shouldClearOverrides = isLeavingManual && hasOverrides;
      // Pass fallbackDriverType only for REVPAR Projection driver
      const fallback = newType === 'REVPAR Projection' ? fallbackDriverType : null;
      // When driver type changes, reset input to 0 so the old value doesn't carry over
      // (e.g., a $POR rate shouldn't persist when switching to Budget driver)
      const driversRequiringInput = ['$ / ORN', '$ / ARN', '% of Revenue', '% of Selected Revenue', '% of Selected Revenue (Budgeted)', '% of Total Revenue', '% of Total Revenue (Budgeted)', '$POR Projection', 'YoY Growth %', '% of Account'];
      const newDriverNeedsInput = driversRequiringInput.includes(newType);
      const inputForNewDriver = newDriverNeedsInput ? parseInput(driverInput) : 0;
      setDriverInput(newDriverNeedsInput ? driverInput : '');
      const srcIds = newType === '% of Account' ? sourceChartOfAccountIds : undefined;
      await onDriverChange(newType, inputForNewDriver, depts, shouldClearOverrides, fallback, srcIds);
      toast.success('Driver updated');
    } catch (error) {
      toast.error('Failed to update driver');
      setDriverType(account.driverType); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Zero Base driver layers apply
  const handleZeroBaseApply = async (layers: ZeroBaseDriverLayer[]) => {
    if (!onDriverChange) return;

    // If switching from Manual, clear overrides (same logic as handleDriverTypeChange)
    const currentEffectiveDriver = account.inheritsFromDepartment && account.departmentDriverType
      ? account.departmentDriverType
      : account.driverType;
    const isLeavingManual = currentEffectiveDriver === 'Manual';
    const hasOverrides = account.monthlyOverrides && Object.values(account.monthlyOverrides).some(v => v === true);
    const shouldClearOverrides = isLeavingManual && hasOverrides;

    setZeroBaseDriverLayers(layers);
    setIsSaving(true);
    try {
      await onDriverChange('Zero Base', null, undefined, shouldClearOverrides, undefined, undefined, layers);
      toast.success('Zero Base drivers applied');
    } catch (error) {
      toast.error('Failed to apply Zero Base drivers');
      setZeroBaseDriverLayers(account.zeroBaseDriverLayers || []);
      setDriverType(account.driverType); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Fetch positions mapped to this GL account (Labor Config driver)
  const openLaborModal = useCallback(async () => {
    setIsLaborModalOpen(true);
    if (laborPositions.length > 0) return; // already loaded
    if (!orgId) return;
    setIsLoadingLaborPositions(true);
    try {
      const mappings = await api.get(
        `${API_ENDPOINTS.LABOR_COA_MAPPINGS}?chartOfAccountId=${account.chartOfAccountId}&orgId=${orgId}`
      );
      const mapped: LaborPosition[] = (mappings.data || []).map((m: any) => ({
        id: m.positionId,
        name: m.positionName,
        departmentName: m.departmentName || '',
      }));
      setLaborPositions(mapped);
    } catch (err) {
      console.error('Failed to load labor positions', err);
      toast.error('Failed to load positions mapped to this account');
    } finally {
      setIsLoadingLaborPositions(false);
    }
  }, [account.chartOfAccountId, orgId, laborPositions.length]);

  // Consume each request once — a remount with the same nonce must not reopen the modal
  const consumedLaborRequest = useRef<number | null>(null);
  useEffect(() => {
    if (!laborModalRequest || laborModalRequest.chartOfAccountId !== account.chartOfAccountId) return;
    if (consumedLaborRequest.current === laborModalRequest.nonce) return;
    consumedLaborRequest.current = laborModalRequest.nonce;
    openLaborModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laborModalRequest?.nonce, account.chartOfAccountId]);

  // Handle Labor Config per-position driver apply
  const handleLaborConfigApply = async (configs: LaborPositionConfig[]) => {
    if (!onDriverChange) return;

    // If switching from Manual, clear overrides (same logic as handleDriverTypeChange)
    const currentEffectiveDriver = account.inheritsFromDepartment && account.departmentDriverType
      ? account.departmentDriverType
      : account.driverType;
    const isLeavingManual = currentEffectiveDriver === 'Manual';
    const hasOverrides = account.monthlyOverrides && Object.values(account.monthlyOverrides).some(v => v === true);
    const shouldClearOverrides = isLeavingManual && hasOverrides;

    setLaborPositionConfigs(configs);
    setIsSaving(true);
    try {
      await onDriverChange('Labor Config', null, undefined, shouldClearOverrides, undefined, undefined, undefined, configs);
      toast.success('Labor drivers applied');
    } catch (error) {
      toast.error('Failed to apply labor drivers');
      setLaborPositionConfigs(account.laborPositionConfigs || []);
      setDriverType(account.driverType); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Handle driver input save
  const handleInputSave = async () => {
    const newValue = parseInput(driverInput);
    const currentValue = account.driverInput;

    // Don't save if value hasn't changed
    if (newValue === currentValue) return;
    if (!onDriverChange) return;

    setIsSaving(true);
    try {
      const depts = (isSelectedRevenueDriver(driverType)) ? selectedDepartments : undefined;
      const fallback = driverFormType === 'REVPAR Projection' ? fallbackDriverType : undefined;
      const srcIds = driverFormType === '% of Account' ? sourceChartOfAccountIds : undefined;
      await onDriverChange(driverType, newValue, depts, undefined, fallback, srcIds);
      toast.success('Driver input saved');
    } catch (error) {
      toast.error('Failed to save driver input');
      setDriverInput(account.driverInput?.toString() || ''); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Handle department selection toggle
  const handleDepartmentToggle = async (deptName: string) => {
    const newSelected = selectedDepartments.includes(deptName)
      ? selectedDepartments.filter(d => d !== deptName)
      : [...selectedDepartments, deptName];

    setSelectedDepartments(newSelected);

    // Save immediately
    if (!onDriverChange) return;

    setIsSaving(true);
    try {
      await onDriverChange(driverType, parseInput(driverInput), newSelected, undefined, undefined);
      toast.success('Department selection updated');
    } catch (error) {
      toast.error('Failed to update department selection');
      setSelectedDepartments(account.selectedDepartments || []); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Handle select/deselect all departments
  const handleToggleAllDepartments = async () => {
    const newSelected = selectedDepartments.length === revenueDepartments.length
      ? []
      : revenueDepartments.map(d => d.name);

    setSelectedDepartments(newSelected);

    // Save immediately
    if (!onDriverChange) return;

    setIsSaving(true);
    try {
      await onDriverChange(driverType, parseInput(driverInput), newSelected, undefined, undefined);
      toast.success('Department selection updated');
    } catch (error) {
      toast.error('Failed to update department selection');
      setSelectedDepartments(account.selectedDepartments || []); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Handle fallback driver type change (for REVPAR Projection)
  const handleFallbackChange = async (newFallback: 'Budget' | 'LY Same Month' | null) => {
    if (!onDriverChange) return;

    setFallbackDriverType(newFallback);
    setIsSaving(true);
    try {
      await onDriverChange(driverType, parseInput(driverInput), undefined, undefined, newFallback);
      toast.success('Fallback driver updated');
    } catch (error) {
      toast.error('Failed to update fallback driver');
      setFallbackDriverType(account.fallbackDriverType || null); // Revert on error
    } finally {
      setIsSaving(false);
    }
  };

  // Check if active month has daily values
  const activeMonthKey = activeMonth ? MONTH_LABELS[activeMonth - 1].toLowerCase() : null;
  const activeMonthHasDailyValues = activeMonthKey ? account.monthlyDailyValues?.[activeMonthKey] === true : false;
  const hasAnyDailyValues = account.hasDailyValues === true;

  return (
    <div ref={tabRootRef} className="space-y-5">
      {/* Daily Entry Active Indicator */}
      {hasAnyDailyValues && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Calendar size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="text-blue-700 font-medium">Daily Entry Active</p>
                <p className="text-blue-600 text-xs mt-1">
                  {activeMonthHasDailyValues && activeMonth
                    ? `${MONTH_LABELS[activeMonth - 1]} has daily values that override the driver calculation`
                    : 'Some months have daily values set'}
                </p>
              </div>
              {onOpenDailyEntry && activeMonth && !isReadOnly && (
                <button
                  onClick={() => onOpenDailyEntry(account, activeMonth)}
                  className="ml-2 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                >
                  {activeMonthHasDailyValues ? 'Edit' : 'Add'} Daily
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Daily Entry Button (when no daily values exist) */}
      {!hasAnyDailyValues && onOpenDailyEntry && activeMonth && !isReadOnly && (
        <button
          onClick={() => onOpenDailyEntry(account, activeMonth)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
        >
          <Calendar size={16} />
          Enter Daily Values for {MONTH_LABELS[activeMonth - 1]}
        </button>
      )}

      {/* Department Driver Inheritance Indicator — the green lives in the
          icon, border and tint; the copy stays on the normal ink ramp so the
          explanatory line stays readable (worst in dark mode). */}
      {account.inheritsFromDepartment && account.departmentDriverType && (
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Info size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-slate-700">
              Inheriting <span className="font-medium">{account.departmentDriverType}</span> from {account.department} department
            </p>
            <p className="text-slate-500 text-xs mt-1">
              Setting an account driver below will override the department driver
            </p>
          </div>
        </div>
      )}

      {/* Driver Selector — searchable grouped picker (see DriverSelect.tsx) */}
      <div>
        <label
          style={{
            display: 'block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '.4px',
            textTransform: 'uppercase',
            color: 'var(--lodge-onyx-500)',
            marginBottom: 8,
          }}
        >
          Driver Type {account.inheritsFromDepartment && <span style={{ fontWeight: 400, color: 'var(--lodge-onyx-400)' }}>(override)</span>}
        </label>
        <DriverSelect
          value={driverType}
          accountType={account.accountType}
          onChange={handleDriverTypeChange}
          disabled={isSaving || isReadOnly}
          isSaving={isSaving}
          accounts={accounts}
          account={account}
        />
      </div>

      {/* Driver Inputs — only shown for drivers that have configurable inputs */}
      {(['$ / ORN', '$ / ARN', '$ / ORN LM', '$ / ARN LM', '$ / ORN LY', '$ / ARN LY',
        '% of Revenue', '% of Selected Revenue', '% of Selected Revenue (Budgeted)',
        '% of Account', 'YoY Growth %', 'REVPAR Projection',
        'Occupancy Forecast', 'Manual', 'Static Entry', 'Budget',
        '% of Total Revenue (Budgeted)', 'Budgeted $POR', 'Budgeted $PAR',
        '$ LM', 'LY Same Month', 'Seasonal Budget Spread', 'OTB Forecast', 'RMS Forecast',
        'HPOR', 'DPOR', 'Avg Rate x Hours',
      ] as string[]).includes(driverFormType) && (
      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-1">Driver Inputs</h4>
        {getVersionDriverEntry(driverType, isBudgetVersion, currentVersion?.budgetReferenceYear)?.inputHint && (
          <p className="text-xs text-slate-500 mb-3">{getVersionDriverEntry(driverType, isBudgetVersion, currentVersion?.budgetReferenceYear)!.inputHint}</p>
        )}

        {/* Room-based drivers with rate input */}
        {(driverFormType === '$ / ORN' || driverFormType === '$ / ARN' ||
          driverFormType === '$ / ORN LM' || driverFormType === '$ / ARN LM' ||
          driverFormType === '$ / ORN LY' || driverFormType === '$ / ARN LY') && (
          <div>
            <label className="block text-xs text-slate-500 mb-1">
              {driverType.includes('ORN') ? '$POR' : '$PAR'} rate
            </label>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
              <button className="px-3 py-2 text-xs text-slate-500 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors">
                Use Global
              </button>
            </div>
            {(driverType.includes('LM') || driverType.includes('LY')) && (
              <p className="text-xs text-slate-400 mt-2">
                Rate applied to {driverType.includes('LM') ? 'last month' : 'last year same month'} room nights.
              </p>
            )}
          </div>
        )}

        {/* Revenue % drivers */}
        {(driverFormType === '% of Revenue' || driverFormType === '% of Selected Revenue') && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Percentage</label>
              <div className="relative">
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            {driverFormType === '% of Revenue' && (
              <p className="text-xs text-slate-400">
                Calculated as {driverInput || 0}% of total revenue.
              </p>
            )}
            {driverFormType === '% of Selected Revenue' && (
              <div className="space-y-3">
                {/* Department Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs text-slate-500">Revenue Departments</label>
                    {revenueDepartments.length > 0 && !isReadOnly && (
                      <button
                        type="button"
                        onClick={handleToggleAllDepartments}
                        disabled={isSaving || isLoadingDepartments}
                        className="text-xs disabled:opacity-50" style={{ color: 'var(--lodge-orange-500)' }}
                      >
                        {selectedDepartments.length === revenueDepartments.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>

                  {isLoadingDepartments ? (
                    <div className="flex items-center justify-center py-4 text-sm text-slate-400">
                      <Loader2 size={16} className="animate-spin mr-2" />
                      Loading departments...
                    </div>
                  ) : revenueDepartments.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {revenueDepartments.map(dept => (
                        <label
                          key={dept.name}
                          className={`relative flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-slate-50 ${isReadOnly || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={{ background: selectedDepartments.includes(dept.name) ? 'var(--lodge-orange-50)' : undefined }}
                        >
                          <div
                            className="w-4 h-4 border rounded flex items-center justify-center"
                            style={selectedDepartments.includes(dept.name)
                              ? { background: 'var(--lodge-orange-400)', borderColor: 'var(--lodge-orange-400)' }
                              : { borderColor: 'var(--fv3-n-200, #cbd5e1)' }}
                          >
                            {selectedDepartments.includes(dept.name) && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={selectedDepartments.includes(dept.name)}
                            onChange={() => handleDepartmentToggle(dept.name)}
                            disabled={isReadOnly || isSaving}
                          />
                          <span className="flex-1 text-sm text-slate-700">{dept.name}</span>
                          <span className="text-xs text-slate-400">{dept.accountCount} accounts</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 py-2">No revenue departments found</p>
                  )}

                  <p className="text-xs text-slate-400 mt-2">
                    {selectedDepartments.length === 0
                      ? 'No departments selected = Total revenue'
                      : `${selectedDepartments.length} department(s) selected`}
                  </p>
                </div>

                <p className="text-xs text-slate-400">
                  Calculated as {driverInput || 0}% of {
                    selectedDepartments.length > 0
                      ? `selected departments (${selectedDepartments.join(', ')})`
                      : 'total revenue'
                  }.
                </p>
              </div>
            )}
          </div>
        )}

        {/* % of Selected Revenue (Budgeted) - department selection only, no percentage input */}
        {driverFormType === '% of Selected Revenue (Budgeted)' && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Calculates the published budget percentage of selected departments and applies it to {isBudgetVersion ? 'budget' : 'forecast'} revenue.
            </p>

            {/* Department Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs text-slate-500">Revenue Departments</label>
                {revenueDepartments.length > 0 && !isReadOnly && (
                  <button
                    type="button"
                    onClick={handleToggleAllDepartments}
                    disabled={isSaving || isLoadingDepartments}
                    className="text-xs disabled:opacity-50" style={{ color: 'var(--lodge-orange-500)' }}
                  >
                    {selectedDepartments.length === revenueDepartments.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {isLoadingDepartments ? (
                <div className="flex items-center justify-center py-4 text-sm text-slate-400">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Loading departments...
                </div>
              ) : revenueDepartments.length > 0 ? (
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {revenueDepartments.map(dept => (
                    <label
                      key={dept.name}
                      className={`relative flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-slate-50 ${isReadOnly || isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ background: selectedDepartments.includes(dept.name) ? 'var(--lodge-orange-50)' : undefined }}
                    >
                      <div
                        className="w-4 h-4 border rounded flex items-center justify-center"
                        style={selectedDepartments.includes(dept.name)
                          ? { background: 'var(--lodge-orange-400)', borderColor: 'var(--lodge-orange-400)' }
                          : { borderColor: 'var(--fv3-n-200, #cbd5e1)' }}
                      >
                        {selectedDepartments.includes(dept.name) && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selectedDepartments.includes(dept.name)}
                        onChange={() => handleDepartmentToggle(dept.name)}
                        disabled={isReadOnly || isSaving}
                      />
                      <span className="flex-1 text-sm text-slate-700">{dept.name}</span>
                      <span className="text-xs text-slate-400">{dept.accountCount} accounts</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-2">No revenue departments found</p>
              )}

              <p className="text-xs text-slate-400 mt-2">
                {selectedDepartments.length === 0
                  ? 'No departments selected = Total revenue'
                  : `${selectedDepartments.length} department(s) selected`}
              </p>
            </div>

            <p className="text-xs text-slate-400">
              Formula: ({LY_BUDGET_DRIVER_BASES[driverType] ? 'LY Published Budget' : 'Published Budget'} ÷ Selected Dept Published Budget Rev) × Selected Dept {isBudgetVersion ? 'Budget' : 'Forecast'} Rev
            </p>
          </div>
        )}

        {/* YoY Growth % driver */}
        {driverFormType === 'YoY Growth %' && (
          <div>
            <label className="block text-xs text-slate-500 mb-1">YoY Growth %</label>
            <div className="relative">
              <input
                type="text"
                value={driverInput}
                onChange={(e) => setDriverInput(e.target.value)}
                onBlur={handleInputSave}
                onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                disabled={isSaving}
                className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50"
                placeholder="0"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Last year's value + {driverInput || 0}% growth.
            </p>
          </div>
        )}

        {/* % of Account driver */}
        {driverFormType === '% of Account' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Percentage</label>
              <div className="relative">
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>

            {/* Source Account Selection — shared picker (stacked search, dept groups) */}
            <SourceAccountPicker
              accounts={accounts.filter(a => a.chartOfAccountId !== account.chartOfAccountId)}
              selectedIds={sourceChartOfAccountIds}
              disabled={isReadOnly || isSaving}
              maxListHeight={320}
              onChange={async (newIds) => {
                setSourceChartOfAccountIds(newIds);
                if (!onDriverChange) return;
                setIsSaving(true);
                try {
                  await onDriverChange(driverType, parseInput(driverInput), undefined, undefined, undefined, newIds);
                  toast.success('Source accounts updated');
                } catch {
                  toast.error('Failed to update source accounts');
                  setSourceChartOfAccountIds(account.sourceChartOfAccountIds || []);
                } finally {
                  setIsSaving(false);
                }
              }}
            />

            <p className="text-xs text-slate-400">
              Forecast = {sourceChartOfAccountIds.length === 0
                ? 'select source accounts above'
                : `sum of ${sourceChartOfAccountIds.length} account(s) × ${driverInput || 0}%`}
            </p>
          </div>
        )}

        {/* REVPAR Projection */}
        {driverFormType === 'REVPAR Projection' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Auto-calculates from hotel revenue data. For the current month, projects based on pace (daily average × days in month).
            </p>

            <div>
              <label className="block text-xs text-slate-500 mb-1">Future Month Fallback</label>
              <select
                value={fallbackDriverType || ''}
                onChange={(e) => {
                  const value = e.target.value as 'Budget' | 'LY Same Month' | '';
                  handleFallbackChange(value === '' ? null : value);
                }}
                disabled={isSaving || isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed bg-white"
              >
                <option value="">None (return $0)</option>
                <option value="Budget">Budget</option>
                <option value="LY Same Month">Last Year Same Month</option>
              </select>
            </div>

            <p className="text-xs text-slate-400">
              For future months with no revenue data yet, use the selected fallback method.
            </p>
          </div>
        )}

        {/* Occupancy Forecast */}
        {driverFormType === 'Occupancy Forecast' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Target Occupancy %</label>
              <div className="relative">
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Calculates room nights based on target occupancy and available rooms.
            </p>
          </div>
        )}

        {/* Manual / Static Entry */}
        {(driverFormType === 'Manual' || driverFormType === 'Static Entry') && (
          <div>
            {driverFormType === 'Static Entry' && (
              <div className="mb-3">
                <label className="block text-xs text-slate-500 mb-1">Fixed Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="text"
                    value={driverInput}
                    onChange={(e) => setDriverInput(e.target.value)}
                    onBlur={handleInputSave}
                    onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                    disabled={isSaving}
                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Same amount applied to all months.
                </p>
              </div>
            )}
            {driverFormType === 'Manual' && (
              <p className="text-sm text-slate-500">
                Manual entry - values are entered directly in the grid or overrides tab.
              </p>
            )}
          </div>
        )}

        {/* Budget */}
        {driverFormType === 'Budget' && (
          <p className="text-sm text-slate-500">
            Values are pulled from the published budget version.
          </p>
        )}

        {/* Seasonal Budget Spread */}
        {driverFormType === 'Seasonal Budget Spread' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Adjustment %</label>
              <div className="relative">
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full px-3 py-2 pr-8 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Follows last year's monthly pattern (actuals, then last year's budget) with an optional growth or decline %.
            </p>
          </div>
        )}

        {/* Budget-based drivers - % of Total Revenue (Budgeted) */}
        {driverFormType === '% of Total Revenue (Budgeted)' && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              Calculates the budget percentage of total revenue and applies it to forecast revenue.
            </p>
            <p className="text-xs text-slate-400">
              Formula: ({LY_BUDGET_DRIVER_BASES[driverType] ? 'LY Published Budget' : 'Published Budget'} ÷ Total Published Budget Revenue) × Total {isBudgetVersion ? 'Budget' : 'Forecast'} Revenue
            </p>
          </div>
        )}

        {/* Budget-based drivers - Budgeted $POR */}
        {driverFormType === 'Budgeted $POR' && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              Uses the budget rate per occupied room and applies it to projected occupancy.
            </p>
            <p className="text-xs text-slate-400">
              Formula: ({LY_BUDGET_DRIVER_BASES[driverType] ? year - 1 : (isBudgetVersion ? (currentVersion?.budgetReferenceYear ?? year) : year)} Budget ÷ {LY_BUDGET_DRIVER_BASES[driverType] ? year - 1 : (isBudgetVersion ? (currentVersion?.budgetReferenceYear ?? year) : year)} Budget Occupied Rooms) × {isBudgetVersion ? 'Budget' : 'Projected'} Occupied Rooms
            </p>
          </div>
        )}

        {/* Budget-based drivers - Budgeted $PAR */}
        {driverFormType === 'Budgeted $PAR' && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              Uses the budget rate per available room and applies it to projected availability.
            </p>
            <p className="text-xs text-slate-400">
              Formula: ({LY_BUDGET_DRIVER_BASES[driverType] ? year - 1 : (isBudgetVersion ? (currentVersion?.budgetReferenceYear ?? year) : year)} Budget ÷ {LY_BUDGET_DRIVER_BASES[driverType] ? year - 1 : (isBudgetVersion ? (currentVersion?.budgetReferenceYear ?? year) : year)} Budget Available Rooms) × {isBudgetVersion ? 'Budget' : 'Projected'} Available Rooms
            </p>
          </div>
        )}

        {/* Last Month drivers (no input needed) */}
        {driverFormType === '$ LM' && (
          <p className="text-sm text-slate-500">
            Values are based on last month's actual amounts.
          </p>
        )}

        {/* Last Year Same Month (no input needed) */}
        {driverFormType === 'LY Same Month' && (
          <p className="text-sm text-slate-500">
            Values are based on the same month from last year.
          </p>
        )}

        {/* External source drivers */}
        {(driverFormType === 'OTB Forecast' || driverFormType === 'RMS Forecast') && (
          <div className="space-y-2">
            <p className="text-sm text-slate-500">
              Values are pulled from {driverFormType === 'OTB Forecast' ? 'On-The-Books (OTB)' : 'Revenue Management System (RMS)'} data.
            </p>
          </div>
        )}

        {/* HPOR - Hours Per Occupied Room */}
        {driverFormType === 'HPOR' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Hours Per Occupied Room</label>
              <input
                type="text"
                value={driverInput}
                onChange={(e) => setDriverInput(e.target.value)}
                onBlur={handleInputSave}
                onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                disabled={isSaving || isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="0.85"
              />
            </div>
            <p className="text-xs text-slate-400">
              Labor Cost = HPOR × Occupied Rooms × Avg Hourly Rate
            </p>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">Labor</span>
              <span className="text-slate-400">Uses historical avg rate from PayrollCost</span>
            </div>
          </div>
        )}

        {/* DPOR - Dollars Per Occupied Room */}
        {driverFormType === 'DPOR' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Dollars Per Occupied Room</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="text"
                  value={driverInput}
                  onChange={(e) => setDriverInput(e.target.value)}
                  onBlur={handleInputSave}
                  onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                  disabled={isSaving || isReadOnly}
                  className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="6.50"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400">
              Labor Cost = DPOR × Projected Occupied Rooms
            </p>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">Labor</span>
              <span className="text-slate-400">Direct cost per room calculation</span>
            </div>
          </div>
        )}

        {/* Avg Rate x Hours */}
        {driverFormType === 'Avg Rate x Hours' && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Projected Hours</label>
              <input
                type="text"
                value={driverInput}
                onChange={(e) => setDriverInput(e.target.value)}
                onBlur={handleInputSave}
                onKeyDown={(e) => e.key === 'Enter' && handleInputSave()}
                disabled={isSaving || isReadOnly}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="2400"
              />
            </div>
            <p className="text-xs text-slate-400">
              Labor Cost = Projected Hours × Avg Hourly Rate
            </p>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded">Labor</span>
              <span className="text-slate-400">FTE-based staffing plan</span>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Zero Base Multi-Driver */}
      {driverFormType === 'Zero Base' && (
        <div className="p-3 bg-orange-50/50 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700">Zero Base Configuration</h4>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.1))', color: 'var(--lodge-orange-500)' }}>
              {zeroBaseDriverLayers.length} LAYER{zeroBaseDriverLayers.length !== 1 ? 'S' : ''}
            </span>
          </div>
          {zeroBaseDriverLayers.length > 0 ? (
            <div className="space-y-1.5 mb-3">
              {zeroBaseDriverLayers.map(layer => (
                <div key={layer.id} className="flex items-center justify-between text-xs text-slate-600 bg-white px-2.5 py-1.5 rounded border border-slate-100">
                  <span className="truncate flex-1">{layer.description || '(untitled)'}</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold ml-2"
                    style={{
                      background: layer.type === 'fixed' ? 'rgba(59,130,246,0.08)' :
                                  layer.type === 'pct_revenue' ? 'rgba(16,185,129,0.08)' :
                                  layer.type === 'pct_other' ? 'rgba(139,92,246,0.08)' :
                                  'var(--fv3-accent-tint, rgba(242,140,56,0.08))',
                      color: layer.type === 'fixed' ? 'var(--fv3-chart-secondary, #3B82F6)' :
                             layer.type === 'pct_revenue' ? '#10B981' :
                             layer.type === 'pct_other' ? '#8B5CF6' :
                             'var(--fv3-accent, #F28C38)',
                    }}>
                    {layer.type === 'fixed' ? 'FIXED' :
                     layer.type === 'pct_revenue' ? '% REV' :
                     layer.type === 'pct_other' ? '% ACCT' :
                     layer.type === 'por' ? 'POR' : 'PAR'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 mb-3">No driver layers configured yet.</p>
          )}
          <button
            onClick={() => setIsZeroBaseModalOpen(true)}
            disabled={isReadOnly}
            className="w-full px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              // --accent stays var(--fv3-accent, #FF7A00) in BOTH modes: a darkened orange fill
              // reads brown on dark surfaces, so only ink/tints shift with the theme.
              background: 'var(--accent, var(--fv3-accent, #FF7A00))',
              color: '#fff',
              boxShadow: '0 1px 3px var(--fv3-accent-shadow, rgba(255,122,0,0.3))',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--fv3-accent-hover, #E56E00)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--accent, var(--fv3-accent, #FF7A00))')}
          >
            {zeroBaseDriverLayers.length > 0 ? 'Edit Driver Layers' : 'Configure Driver Layers'}
          </button>
        </div>
      )}

      {/* Zero Base Modal */}
      <ZeroBaseDriverModal
        isOpen={isZeroBaseModalOpen}
        onClose={() => {
          setIsZeroBaseModalOpen(false);
          // Revert driver type if no layers were configured yet
          if (zeroBaseDriverLayers.length === 0 && account.driverType !== 'Zero Base') {
            setDriverType(account.driverType);
          }
        }}
        onApply={handleZeroBaseApply}
        initialLayers={zeroBaseDriverLayers}
        accountName={account.accountName}
        accountNumber={account.accountNumber}
        department={account.department}
        accounts={accounts}
        occupiedRoomsPerMonth={occupiedRoomsPerMonth}
        availableRoomsPerMonth={availableRoomsPerMonth}
      />

      {/* Labor Config — per-position driver */}
      {driverFormType === 'Labor Config' && (
        <div className="p-3 bg-rose-50/50 rounded-lg border border-rose-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700">Labor Driver Configuration</h4>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{ background: 'var(--fv3-accent-tint, rgba(242,140,56,0.1))', color: 'var(--lodge-orange-500)' }}>
              {laborPositionConfigs.filter(c => c.value && c.value > 0).length} CONFIGURED
            </span>
          </div>
          <button
            onClick={openLaborModal}
            disabled={isReadOnly}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              // --accent stays var(--fv3-accent, #FF7A00) in BOTH modes: a darkened orange fill
              // reads brown on dark surfaces, so only ink/tints shift with the theme.
              background: 'var(--accent, var(--fv3-accent, #FF7A00))',
              color: '#fff',
              boxShadow: '0 1px 3px var(--fv3-accent-shadow, rgba(255,122,0,0.3))',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--fv3-accent-hover, #E56E00)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'var(--accent, var(--fv3-accent, #FF7A00))')}
          >
            <Users size={14} />
            {laborPositionConfigs.length > 0 ? 'Edit Position Drivers' : 'Configure Positions'}
          </button>
          <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
            Set min/occ, hrs/occ, or % revenue per position mapped to this account via
            Position → GL Mapping.
          </p>
        </div>
      )}

      {/* Labor Config Modal */}
      <LaborForecastDriverModal
        isOpen={isLaborModalOpen}
        onClose={() => {
          setIsLaborModalOpen(false);
          // Revert driver type if no positions were configured yet
          if (laborPositionConfigs.length === 0 && account.driverType !== 'Labor Config') {
            setDriverType(account.driverType);
          }
        }}
        onApply={handleLaborConfigApply}
        initialConfigs={laborPositionConfigs}
        accountName={account.accountName}
        accountNumber={account.accountNumber}
        department={account.department}
        positions={laborPositions}
        isLoadingPositions={isLoadingLaborPositions}
        accounts={accounts}
        revenueDepartments={laborRevenueDepartments}
        occupiedRoomsPerMonth={occupiedRoomsPerMonth}
      />


      {/* Number Explanation */}
      <CalculationExplanationBox key={`${account.chartOfAccountId}-${activeMonth}`} account={account} activeMonth={activeMonth} onViewTransactions={onViewTransactions} currency={currency} />
    </div>
  );
}

interface OverridesTabContentProps {
  account: ForecastAccount;
  onOverrideChange?: (monthIndex: number, value: number | null) => Promise<void>;
  isReadOnly?: boolean;
}

function OverridesTabContent({ account, onOverrideChange, isReadOnly = false }: OverridesTabContentProps) {
  const [applyAllValue, setApplyAllValue] = useState('');
  const [isApplyingAll, setIsApplyingAll] = useState(false);

  const [values, setValues] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    MONTH_LABELS.forEach((month, index) => {
      const monthKey = month.toLowerCase() as keyof typeof account.monthlyValues;
      // Only show value if there's an actual override for this month
      const hasOverride = account.monthlyOverrides?.[monthKey] === true;
      if (hasOverride) {
        const value = account.monthlyValues[monthKey];
        initial[index] = value?.toString() || '';
      } else {
        initial[index] = '';
      }
    });
    return initial;
  });
  const [savingMonth, setSavingMonth] = useState<number | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // Sync local state when account changes (fixes stale state when switching accounts)
  // Use account.total as a stable proxy for monthlyValues changes to avoid infinite loops
  useEffect(() => {
    const newValues: Record<number, string> = {};
    MONTH_LABELS.forEach((month, index) => {
      const monthKey = month.toLowerCase() as keyof typeof account.monthlyValues;
      // Only show value if there's an actual override for this month
      const hasOverride = account.monthlyOverrides?.[monthKey] === true;
      if (hasOverride) {
        const value = account.monthlyValues[monthKey];
        newValues[index] = value?.toString() || '';
      } else {
        newValues[index] = '';
      }
    });
    setValues(newValues);
  }, [account.id, account.total]);

  const parseValue = (str: string): number | null => {
    const cleaned = str.replace(/[$,\s]/g, '');
    if (cleaned === '' || cleaned === '-') return null;
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? null : parsed;
  };

  const handleSave = async (monthIndex: number) => {
    const newValue = parseValue(values[monthIndex]);
    const originalValue = account.monthlyValues[MONTH_LABELS[monthIndex].toLowerCase() as keyof typeof account.monthlyValues];

    // Don't save if value hasn't changed
    if (newValue === originalValue) return;
    if (!onOverrideChange) return;

    setSavingMonth(monthIndex);
    try {
      await onOverrideChange(monthIndex, newValue);
      toast.success(`${MONTH_LABELS[monthIndex]} override saved`);
    } catch (error) {
      toast.error(`Failed to save ${MONTH_LABELS[monthIndex]} override`);
      // Revert on error
      setValues(prev => ({
        ...prev,
        [monthIndex]: originalValue?.toString() || ''
      }));
    } finally {
      setSavingMonth(null);
    }
  };

  const handleClearAll = async () => {
    if (!onOverrideChange || isReadOnly) return;

    // Only clear months that actually have overrides
    const monthsWithOverrides = MONTH_LABELS
      .map((month, index) => ({ month, index }))
      .filter(({ month }) => {
        const monthKey = month.toLowerCase() as keyof typeof account.monthlyOverrides;
        return account.monthlyOverrides?.[monthKey] === true;
      });

    if (monthsWithOverrides.length === 0) {
      toast.success('No overrides to clear');
      return;
    }

    setIsClearing(true);
    try {
      // Clear only months that have overrides
      const clearPromises = monthsWithOverrides.map(({ index }) =>
        onOverrideChange(index, null)
      );
      await Promise.all(clearPromises);

      // Clear local state
      const clearedValues: Record<number, string> = {};
      MONTH_LABELS.forEach((_, index) => {
        clearedValues[index] = '';
      });
      setValues(clearedValues);

      toast.success('All overrides cleared');
    } catch (error) {
      toast.error('Failed to clear overrides');
    } finally {
      setIsClearing(false);
    }
  };

  const handleApplyAll = async () => {
    const parsed = parseValue(applyAllValue);
    if (parsed === null || !onOverrideChange || isReadOnly) return;
    setIsApplyingAll(true);
    try {
      await Promise.all(MONTH_LABELS.map((_, index) => onOverrideChange(index, parsed)));
      const filled: Record<number, string> = {};
      MONTH_LABELS.forEach((_, index) => { filled[index] = parsed.toString(); });
      setValues(filled);
      setApplyAllValue('');
    } catch {
      toast.error('Failed to apply overrides');
    } finally {
      setIsApplyingAll(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-700">Month Overrides</h4>
        <button
          onClick={handleClearAll}
          disabled={isClearing || isReadOnly}
          className="text-xs text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isClearing ? 'Clearing...' : 'Clear All'}
        </button>
      </div>
      {/* Apply a single value to all 12 months */}
      {!isReadOnly && (
        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200">
          <div className="relative flex-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
            <input
              type="text"
              placeholder="Amount for all months"
              value={applyAllValue}
              onChange={(e) => setApplyAllValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyAll()}
              disabled={isApplyingAll}
              className="w-full pl-5 pr-2 py-1.5 text-sm border border-slate-200 rounded bg-white text-right focus:outline-none focus:outline-none disabled:opacity-50"
            />
          </div>
          <button
            onClick={handleApplyAll}
            disabled={!applyAllValue || isApplyingAll}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
          >
            {isApplyingAll ? <Loader2 size={12} className="animate-spin" /> : null}
            Apply all
          </button>
        </div>
      )}
      <div className="space-y-2">
        {MONTH_LABELS.map((month, index) => (
          <div
            key={month}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <span className="text-sm text-slate-600 w-10">{month}</span>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="—"
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded text-right focus:outline-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                value={values[index]}
                onChange={(e) => setValues(prev => ({ ...prev, [index]: e.target.value }))}
                onBlur={() => handleSave(index)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave(index)}
                disabled={savingMonth === index || isReadOnly}
              />
              {savingMonth === index && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Loader2 size={12} className="animate-spin" style={{ color: 'var(--lodge-orange-400)' }} />
                </div>
              )}
            </div>
            <button className="p-1 text-slate-400 hover:text-slate-600 transition-colors">
              <MessageSquare size={14} />
            </button>
          </div>
        ))}
      </div>
      <button className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded-lg transition-colors">
        Apply driver to range...
      </button>
    </div>
  );
}

interface NotesTabContentProps {
  account: ForecastAccount;
}

function NotesTabContent({ account }: NotesTabContentProps) {
  const { state } = useForecastV3();
  const { orgId, locationId, currentVersion, year, activeMonth } = state;
  const { userSettings } = useUserSettingsContext() as { userSettings: { email?: string } | null };
  const currentUserEmail = userSettings?.email || '';

  // Import the events components and hook dynamically to avoid circular deps
  const { EventTimeline, NoteComposer } = require('./notes');
  const { useAccountEvents } = require('../../hooks/useAccountEvents');

  const versionId = currentVersion?.id;

  const {
    events,
    isLoading,
    isError,
    addNote,
    deleteEvent,
    isAddingNote,
    isDeletingEvent,
  } = useAccountEvents({
    orgId: orgId || 0,
    locationId: locationId || 0,
    versionId: versionId || 0,
    accountId: account.chartOfAccountId,
    year,
    enabled: !!orgId && !!locationId && !!versionId,
  });

  // Auto-scroll to bottom when events load or new event added
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const prevEventsLength = React.useRef(events.length);

  React.useEffect(() => {
    // Scroll to bottom when events first load or when new events are added
    if (timelineRef.current && events.length > 0) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
    prevEventsLength.current = events.length;
  }, [events.length, isLoading]);

  const handleAddNote = async (content: string) => {
    try {
      await addNote(content);
      toast.success('Note added');
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Failed to add note');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      toast.success('Note deleted');
    } catch (err) {
      console.error('Failed to delete event:', err);
      toast.error('Failed to delete note');
    }
  };

  if (!versionId) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400 text-sm">
        Select a version to view notes
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Timeline (scrollable) - chronological order, newest at bottom */}
      <div className="flex-1 overflow-y-auto pt-3" ref={timelineRef}>
        <EventTimeline
          events={events}
          isLoading={isLoading}
          isError={isError}
          onDeleteEvent={handleDeleteEvent}
          isDeletingEvent={isDeletingEvent}
          emptyMessage="No activity yet. Add a note or make changes to see the history."
          currentUserEmail={currentUserEmail}
          chronological
        />
      </div>

      {/* Note composer at bottom */}
      <div className="pt-3 border-t border-slate-100">
        <NoteComposer
          onSubmit={handleAddNote}
          isSubmitting={isAddingNote}
          placeholder="Add a note about this account..."
        />
      </div>
    </div>
  );
}

export default AccountDetailPanel;
